import React, {Component} from 'react';
import ReactToPrint from 'react-to-print';
import PreSettlementReportFilter from '../components/PreSettlementReportsFilter';
import ReportHeaderLocation from '../components/ReportHeaderLocation';
import SettlementReportCategory from '../components/SettlementReportCategory';
import SettlementReportInventory from '../components/SettlementReportInventory';
import SettlementReportSales from '../components/SettlementReportSales';
import NoAccess from '../noAccess';
import {getTimeZoneOffsetDefault} from '../shared/dateTimeLists';
import {hasReportsAccess} from '../shared/authFunctions';
import FuseTheme from '../global/theme';
import {ViewTitle} from 'admin-on-rest/lib/mui';
import {Card, CardText} from 'material-ui/Card';
import CircularProgress from 'material-ui/CircularProgress';
import RaisedButton from 'material-ui/RaisedButton';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/editor/insert-chart';

export const fusePreSettlementReportsIcon = Icon;

const styles = {
    button: {color: FuseTheme.palette.primary3Color},
    reportHeader: {
        margin: '0 0 5px 0',
    },
    reportNote: {fontStyle: 'italic'},
};

/**
 * Pre-Settlement Reports class for retrieving and displaying Sales Summary and Inventory Summary
 *
 */
export class PreSettlementReports extends Component {
    constructor(props) {
        super(props);
        const offsetDefault = getTimeZoneOffsetDefault();
        this.state = {
            location: {
                dbaPriceLevel: null,
                id: null,
                name: null,
                priceLevel: null,
                productType: null
            },
            inventorySummary: [],
            inventorySummaryComplete: false,
            messageInventory: null,
            messageSales: null,
            reportNote: '',
            salesSummaryComplete: false,
            selectedTimeZoneOffset: offsetDefault,
            selectedZeroDay: null,
            system: '',
        };
    }

    /**
     * Handle event props from PreSettlementReportFilter
     *
     * @param type
     * @param value
     */
    handleFilterSelect = (type, value) => {
        // Store values for selected Location object and its attributes
        if (type === 'location') {
            // Gather Price Level and Product Type data from Location and DBA
            let locationDBAPriceLevel = null;
            let locationPriceLevel = null;
            let locationProductType = null;
            const dba = value.dba;
            if (dba) {
                const dbaPriceLevel = dba.price_level;
                if (dbaPriceLevel && dbaPriceLevel.name) {
                    locationDBAPriceLevel = dbaPriceLevel.name;
                }
                const dbaProductType = dba.product_type;
                if (dbaProductType) {
                    locationProductType = dbaProductType;
                }
            }
            const priceLevel = value.price_level;
            if (priceLevel && priceLevel.name) {
                locationPriceLevel = priceLevel.name;
            }
            // Store the entire Location with calculated values
            const location = value;
            location.dbaPriceLevel = locationDBAPriceLevel;
            location.priceLevel = locationPriceLevel;
            location.productType = locationProductType;

            const system = localStorage.getItem('system');

            // Set Location for PreSettlementReportInventory and PreSettlementReportsales components
            this.setState({
                inventorySummaryComplete: false,
                location: location,
                selectedZeroDay: null,
                system: system
            });
        }

        // Set Zero Day selection for PreSettlementReportInventory and PreSettlementReportsales components
        if (type === 'zero_day') {
            this.setState({
                inventorySummaryComplete: false,
                selectedZeroDay: value
            });
        }

        // Set Time Zone Offset selection for PreSettlementReportsales component
        if (type === 'time_zone_offset') {
            this.setState({
                selectedTimeZoneOffset: value
            });
        }
    };

    /**
     * Handle event props for Sales Summary table completion
     *
     * @param isComplete
     */
    handleSalesSummaryComplete = (isComplete) => {
        this.setState({
            salesSummaryComplete: isComplete
        });
    };

    /**
     * Handle event props for Inventory Summary table completion
     *
     * @param isComplete
     */
    handleInventorySummaryComplete = (isComplete) => {
        this.setState({
            inventorySummaryComplete: isComplete
        });
    };

    /**
     * Handle Inventory Summary table data
     *
     * @param data
     */
    handleInventoryData = (data) => {
        this.setState({
            inventorySummary: data
        });
    };

    /**
     * Handle event props for Sales Summary messaging
     *
     * @param message
     */
    handleSalesMessage = (message) => {
        this.setState({
            messageSales: message
        });
    };

    /**
     * Handle event props for Inventory Summary messaging
     *
     * @param message
     */
    handleInventoryMessage = (message) => {
        this.setState({
            messageInventory: message
        });
    };

    render() {
        const {
            inventorySummary,
            inventorySummaryComplete,
            location,
            messageSales,
            messageInventory,
            salesSummaryComplete,
            selectedZeroDay,
            selectedTimeZoneOffset,
            system
        } = this.state;

        if (!hasReportsAccess()) return <NoAccess/>;

        const ListTitle = <span className="list-title hide-print">
            <Avatar icon={<Icon/>} className="avatar-title"/>Pre-Settlement Reports</span>;

        const ListDescription = () => {
            return (
                <span className="list-description hide-print">
                    <p>A Pre-Settlement Report displays sales and inventory status for a specific Location with an open
                        Final Audit.</p>
                    <p>The Report monitors gains, losses and discrepancies, to determine commission payment for
                        Location operators.</p>
                </span>
            );
        };

        return (
            <div className="lists-container" ref={e => (this.printReportRef = e)}>
                <Card>
                    <ViewTitle title={ListTitle}/>
                    <CardText>
                        <ListDescription/>

                        {/* Pre-Settlement Report Filter (Location and Zero Day) */}
                        <PreSettlementReportFilter
                            onSelectLocation={this.handleFilterSelect}
                            onSelectTimeZoneOffset={this.handleFilterSelect}
                            onSelectZeroDay={this.handleFilterSelect}
                        />

                        {/* Print Header */}
                        {location && selectedZeroDay &&
                        <ReportHeaderLocation
                            location={location}
                            zeroDay={selectedZeroDay}
                            reportType="Pre-Settlement Report"
                        />
                        }

                        {/* Pre-Settlement Report Content */}
                        {location && location.id && selectedZeroDay && selectedTimeZoneOffset &&
                        <div>

                            {/* Pre-Settlement Report Header, Loader, and messaging */}
                            <div className="list-header hide-print report-header" style={styles.reportHeader}>
                                <div className="btn-print-container report-header-button-container">
                                    <ReactToPrint
                                        trigger={() =>
                                            <RaisedButton
                                                primary
                                                type="button"
                                                label="Print Report"
                                                style={styles.button}
                                            />
                                        }
                                        content={() => this.printReportRef}
                                    />
                                </div>
                                <h2 className="report-header-title">Pre-Settlement Report</h2>
                                <p>
                                    <span className="bold">Store: </span>
                                    {location.id} - {location.name} - {location.city}, {location.state}
                                    <br/>
                                    <span className="bold">Zero Day: </span>{selectedZeroDay}
                                    <br/>
                                    <span className="bold">Time Zone Offset: </span>{selectedTimeZoneOffset}
                                </p>
                                {messageSales &&
                                <p style={styles.reportNote}>
                                    {messageSales}
                                </p>
                                }
                                {messageInventory &&
                                <p style={styles.reportNote}>
                                    {messageInventory}
                                </p>
                                }
                            </div>

                            {!salesSummaryComplete && !inventorySummaryComplete &&
                            <div className="loader-container">
                                <h4>Generating Report...</h4>
                                <CircularProgress
                                    size={24}
                                    thickness={2}
                                    className="loader"
                                />
                            </div>
                            }

                            {/* Sales Summary */}
                            <SettlementReportSales
                                reportType="pre-settlement"
                                reportName="Pre-Settlement-Report"
                                location={location}
                                onSalesComplete={this.handleSalesSummaryComplete}
                                onDisplayMessage={this.handleSalesMessage}
                                system={system}
                                timeZoneOffset={selectedTimeZoneOffset}
                                zeroDay={selectedZeroDay}
                            />

                            {/* Inventory Summary */}
                            <SettlementReportInventory
                                reportType="pre-settlement"
                                reportName="Pre-Settlement-Report"
                                location={location}
                                onInventoryData={this.handleInventoryData}
                                onInventoryComplete={this.handleInventorySummaryComplete}
                                onDisplayMessage={this.handleInventoryMessage}
                                system={system}
                                zeroDay={selectedZeroDay}
                            />

                            {/* Category Audit Info */}
                            {inventorySummaryComplete && inventorySummary && inventorySummary.length > 0 &&
                                <SettlementReportCategory
                                    inventoryList={inventorySummary}
                                    location={location}
                                    system={system}
                                    zeroDay={selectedZeroDay}
                                />
                            }
                        </div>
                        }
                    </CardText>
                </Card>
            </div>
        )
    }
}



// WEBPACK FOOTER //
// ./src/Fuse/Report/PreSettlementReports.js